import { useEffect, useMemo, useState } from 'react'
import { loadCurrentDueDateConfig, type DueDateConfigSource } from '../../lib/dueDateConfig'
import { allowedDaysForGroup, buildMonthCells, normalizeGroupCode, type DueDatePayload } from '../../lib/dueDateGroups'
import './VencimentoGrupo.css'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

function formatDaysSentence(days: number[]) {
  const formatted = days.map((day) => String(day).padStart(2, '0'))
  if (formatted.length <= 1) return formatted[0] ?? ''
  return `${formatted.slice(0, -1).join(', ')} ou ${formatted.at(-1)}`
}

export function VencimentoGrupo() {
  const now = new Date()
  const [payload, setPayload] = useState<DueDatePayload | null>(null)
  const [source, setSource] = useState<DueDateConfigSource>('fallback')
  const [groupInput, setGroupInput] = useState('')
  const [resolvedGroup, setResolvedGroup] = useState('')
  const [monthCursor, setMonthCursor] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1))
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    loadCurrentDueDateConfig()
      .then(({ config, source: loadedSource }) => {
        if (!mounted) return
        setPayload(config.payload)
        setSource(loadedSource)
      })
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setResolvedGroup(normalizeGroupCode(groupInput))
      setSelectedDay(null)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [groupInput])

  const allowedDays = useMemo(() => payload && resolvedGroup ? allowedDaysForGroup(payload, resolvedGroup) : null, [payload, resolvedGroup])
  const cells = useMemo(() => buildMonthCells(monthCursor.getFullYear(), monthCursor.getMonth()), [monthCursor])
  const hasInput = groupInput.trim().length > 0
  const invalidFormat = hasInput && !resolvedGroup
  const groupNotFound = Boolean(payload && resolvedGroup && !allowedDays)

  function moveMonth(offset: number) {
    setMonthCursor((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1))
    setSelectedDay(null)
  }

  function handleGroupChange(value: string) {
    setGroupInput(value.replace(/\D/g, ''))
  }

  const selectedLabel = selectedDay
    ? `${String(selectedDay).padStart(2, '0')}/${String(monthCursor.getMonth() + 1).padStart(2, '0')}/${monthCursor.getFullYear()}`
    : ''

  return (
    <section className="operational-tool due-date-tool">
      <div className="tool-heading due-date-heading">
        <div>
          <p className="eyebrow">FERRAMENTA OPERACIONAL</p>
          <h2>Vencimento por Grupo</h2>
          <p className="muted">Consulte os vencimentos disponíveis pelo Grupo de Leitura.</p>
        </div>
        {source === 'cache' && <span className="status-chip warning">Contingência offline</span>}
      </div>

      <div className="due-date-holder-alert" role="note" aria-label="Regra de titularidade">
        <span className="due-date-holder-alert__icon">!</span>
        <div><strong>Serviço restrito ao titular</strong><p>A alteração do vencimento só pode ser solicitada pelo titular da matrícula.</p></div>
      </div>

      <div className="due-date-layout">
        <section className="tool-card due-date-query-card">
          <div className="due-date-query-primary">
            <label className="due-date-group-field">
              <span>Grupo de Leitura</span>
              <input
                inputMode="numeric"
                autoComplete="off"
                value={groupInput}
                onChange={(event) => handleGroupChange(event.target.value)}
                placeholder="Ex.: 60"
                aria-describedby="due-date-group-help"
              />
            </label>
            <small id="due-date-group-help" className="muted">Digite somente o número. A consulta é automática.</small>
          </div>

          {loading && <div className="tool-status info">Carregando regra operacional...</div>}
          {!loading && !hasInput && <div className="due-date-empty-hint">Informe o grupo para visualizar os vencimentos permitidos.</div>}
          {!loading && invalidFormat && <div className="tool-status error">Informe um Grupo de Leitura válido.</div>}
          {!loading && groupNotFound && <div className="tool-status error">Grupo de Leitura não localizado. Verifique o número informado.</div>}

          {!loading && allowedDays && (
            <div className="due-date-group-result" aria-live="polite">
              <p className="due-date-result-kicker">Grupo {resolvedGroup} localizado</p>
              <h3>Você pode oferecer vencimento nos dias {formatDaysSentence(allowedDays)}.</h3>
              <div className="due-date-day-chips" aria-label="Dias de vencimento permitidos">
                {allowedDays.map((day) => (
                  <button
                    type="button"
                    key={day}
                    className={selectedDay === day ? 'is-selected' : ''}
                    onClick={() => setSelectedDay(day)}
                    aria-pressed={selectedDay === day}
                    aria-label={`Selecionar vencimento dia ${day}`}
                  >
                    {String(day).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="due-date-rule-meta"><strong>Consulta Rápida para Vencimentos</strong></div>
        </section>

        <section className={`tool-card due-date-calendar-card ${allowedDays ? 'is-ready' : ''}`} aria-label="Calendário de vencimentos disponíveis">
          <div className="due-date-calendar-head">
            <button type="button" onClick={() => moveMonth(-1)} aria-label="Mês anterior">‹</button>
            <div><strong>{MONTHS[monthCursor.getMonth()]}</strong><span>{monthCursor.getFullYear()}</span></div>
            <button type="button" onClick={() => moveMonth(1)} aria-label="Próximo mês">›</button>
          </div>

          <div className="due-date-weekdays" aria-hidden="true">
            {WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
          </div>

          <div className="due-date-calendar-grid">
            {cells.map((day, index) => {
              if (day == null) return <span className="due-date-day is-empty" key={`empty-${index}`} />
              const isAllowed = Boolean(allowedDays?.includes(day))
              const isSelected = isAllowed && selectedDay === day
              return (
                <button
                  type="button"
                  key={day}
                  className={`due-date-day ${isAllowed ? 'is-allowed' : ''} ${isSelected ? 'is-selected' : ''}`}
                  disabled={!isAllowed}
                  onClick={() => setSelectedDay(day)}
                  aria-label={isAllowed ? `Dia ${day}, vencimento permitido` : `Dia ${day}, indisponível`}
                  aria-pressed={isSelected}
                >
                  <span>{day}</span>{isSelected && <small>✓</small>}
                </button>
              )
            })}
          </div>

          <div className="due-date-calendar-footer">
            <div className="due-date-calendar-legend">
              <span><i className="allowed" /> Permitido</span>
              <span><i className="selected" /> Selecionado</span>
            </div>
            {selectedLabel && <div className="due-date-selected" role="status"><span>Selecionado</span><strong>{selectedLabel}</strong></div>}
          </div>
        </section>
      </div>
    </section>
  )
}
